import { CustomerInfoPairBox } from "@/features/product-and-price/info-boxes/customer/CustomerInfoPairBox";
import { CustomerBox } from "@/features/product-and-price/info-boxes/types";
import { GapValues } from "@/lib/utils/GapValues";
import { ApolloError } from "@apollo/client";
import { Row } from "@stenajs-webui/core";
import {
  Card,
  CardBody,
  CardHeader,
  FlatButton,
} from "@stenajs-webui/elements";
import { StandardTable } from "@stenajs-webui/grid";
import { formatDateString } from "@/lib/dates/formatters";
import * as React from "react";
import { useCallback, useMemo } from "react";
import { Banner } from "@stenajs-webui/elements";
import { Box } from "@stenajs-webui/core";
import { createParentHistoryTableConfig } from "../config/ParentHistoryTableConfig";
import { useParentEditor } from "../hooks/UseParentEditor";
import {
  ParentStretchKey,
  useParentMutations,
} from "../hooks/UseParentMutations";
import { canAddParent } from "../lib/parentActionEligibility";
import { ParentHistoryEntry, ParentChange } from "../types";
import { CustomerParentEditorModal } from "./CustomerParentEditorModal";

interface Props {
  customer: CustomerBox;
  history: ParentHistoryEntry[];
  error: ApolloError | undefined;
}

export const CustomerPriceParent: React.FC<Props> = ({
  customer,
  history,
  error,
}) => {
  const currentParentNo = customer.parent?.id;
  /** Which stretch is in force, and so what each row allows. */
  const today = formatDateString(new Date());
  const { editorOpen, openEditor, closeEditor } = useParentEditor();
  const {
    addParent,
    setParentValidTo,
    deleteParent,
    loading: saving,
    error: saveError,
  } = useParentMutations(customer.custNo);

  /**
   * The writes return nothing but success, so the table catches up by the
   * read running again. Whatever the backend made of the change is then
   * what is on screen, rather than a guess at it.
   *
   * Each hands its promise back so whatever started the write can hold a
   * spinner until it is done and close itself afterwards. A failure is
   * swallowed here because useMutation has already kept it for the banner
   * above - rethrowing would only close the thing that reports it.
   */
  const onSetEnd = useCallback(
    (key: ParentStretchKey, validTo: string) =>
      setParentValidTo(key, validTo).catch(() => undefined),
    [setParentValidTo]
  );

  const onDelete = useCallback(
    (key: ParentStretchKey) => deleteParent(key).catch(() => undefined),
    [deleteParent]
  );

  const config = useMemo(
    () => createParentHistoryTableConfig(history, today, onSetEnd, onDelete),
    [history, today, onSetEnd, onDelete]
  );

  const onSave = async (change: ParentChange) => {
    await addParent(Number(change.parentCustomerNo), change.validFrom).catch(
      () => undefined
    );
    closeEditor();
  };

  return (
    <>
      <CustomerInfoPairBox customer={customer} parentChildLinkDisabled />

      {saveError && (
        <Box spacing={1}>
          <Banner
            variant={"error"}
            headerText={"The change was not saved"}
            text={saveError.message}
          />
        </Box>
      )}

      <Card>
        <CardHeader
          text={"Parent history"}
          contentRight={
            <Row gap={GapValues.EIGHT}>
              {/*
                Disabled while any write is in flight. The spinner for one
                sits on the button that started it, inside the editor or
                the row's popover - this only stops a second change being
                started from out here while one is still going.
              */}
              <FlatButton
                label={"Add parent"}
                disabled={!canAddParent(customer) || saving}
                onClick={openEditor}
              />
            </Row>
          }
        />
        <CardBody>
          {/*
            No loading prop. The table swaps its whole body for a spinner
            when given one, so the rows would vanish on every save and come
            back a moment later. The rows stay, and change when the refetch
            that follows the write lands.
          */}
          <StandardTable
            config={config}
            items={history}
            error={error}
            noItemsLabel={"This account has never had a parent."}
          />
        </CardBody>
      </Card>

      {editorOpen && (
        <CustomerParentEditorModal
          currentParentNo={currentParentNo}
          history={history}
          onSave={onSave}
          onRequestClose={closeEditor}
        />
      )}
    </>
  );
};
